import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import {
  CaretRightIcon,
  PauseIcon,
  PlayIcon,
  UndoIcon,
} from '../../../assets/svg/SVGComponents';
import { IBoardSnapshot } from '../../../dtos/IBoardSnapshot';
import TournamentBoard, { IBoardSourceHandlers, TBoardSource } from '..';
import { TConnectionStatus } from '../logic/BoardConnection';
import { colors } from '../styles';
import DemoTournament from './DemoTournament';
import { DEMO_STEPS, IDemoControls } from './demoSteps';

const CHANNEL = 'telao-demo';

type TMessage =
  | { type: 'hello' }
  | { type: 'reset'; snapshot: IBoardSnapshot }
  | { type: 'snapshot'; snapshot: IBoardSnapshot }
  | { type: 'status'; status: TConnectionStatus };

const glass = `
  background: rgba(0, 18, 42, 0.86);
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(18px) saturate(160%);
  color: #fff;
  font-family: 'RedHatDisplay-Regular', sans-serif;
`;

/** Palco da demo: painel | telão | celular — nada fica por cima do telão. */
const Stage = styled.div<{ $dock: boolean; $phone: boolean }>`
  display: grid;
  grid-template-columns:
    ${({ $dock }) => ($dock ? '22rem' : '0')}
    minmax(0, 1fr)
    ${({ $phone }) => ($phone ? '16rem' : '0')};
  height: 100vh;
  background: #000a18;

  @media (max-width: 900px) {
    grid-template-columns: 0 minmax(0, 1fr) 0;
  }
`;

const BoardCell = styled.div`
  position: relative;
  min-width: 0;
  overflow: hidden;
`;

const Dock = styled(motion.aside)`
  ${glass}
  box-shadow: none;
  border-width: 0 1px 0 0;
  height: 100vh;
  overflow: auto;
  padding: 1.1rem 1rem;
  display: flex;
  flex-direction: column;

  h3 {
    margin: 0 0 0.2rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: 1.05rem;
  }

  > p {
    margin: 0 0 0.8rem;
    font-size: 0.8rem;
    color: ${colors.muted};
  }

  @media (max-width: 900px) {
    position: fixed;
    z-index: 60;
    inset: 0 auto 0 0;
    width: min(22rem, 100vw);
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-bottom: 0.8rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ $primary?: boolean; $on?: boolean }>`
  border: 1px solid
    ${({ $primary, $on }) =>
      $primary || $on ? colors.accent : 'rgba(255, 255, 255, 0.2)'};
  background: ${({ $primary }) =>
    $primary ? colors.accent : 'rgba(255, 255, 255, 0.06)'};
  color: ${({ $primary, $on }) =>
    $primary ? '#1a1a1a' : $on ? colors.accent : '#fff'};
  border-radius: 999px;
  padding: 0.45rem 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.82rem;
  cursor: pointer;
  transition:
    transform 100ms ease-out,
    background 150ms;

  svg {
    width: 1rem;
    height: 1rem;
    flex: none;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
  }

  &:active {
    transform: scale(0.96);
  }
`;

const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.3rem;
`;

const StepButton = styled.button<{ $active: boolean }>`
  position: relative;
  overflow: hidden;
  width: 100%;
  display: grid;
  grid-template-columns: 1.6rem 1fr;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
  border: 1px solid
    ${({ $active }) => ($active ? colors.accent : 'transparent')};
  background: ${({ $active }) =>
    $active ? 'rgba(255, 152, 0, 0.14)' : 'rgba(255, 255, 255, 0.04)'};
  color: #fff;
  border-radius: 0.7rem;
  padding: 0.5rem 0.6rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.88rem;
  cursor: pointer;
  transition: transform 100ms ease-out;

  &:active {
    transform: scale(0.98);
  }

  b {
    display: grid;
    place-items: center;
    width: 1.6rem;
    height: 1.6rem;
    border-radius: 50%;
    font-size: 0.75rem;
    background: ${({ $active }) =>
      $active ? colors.accent : 'rgba(255, 255, 255, 0.12)'};
    color: ${({ $active }) => ($active ? '#1a1a1a' : '#fff')};
  }
`;

const StepProgress = styled(motion.span)`
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  width: 100%;
  background: ${colors.accent};
  transform-origin: left center;
`;

const Keys = styled.p`
  margin: 0.8rem 0 0 !important;
  font-size: 0.72rem !important;

  kbd {
    display: inline-block;
    padding: 0 0.35em;
    margin: 0 0.1em;
    border-radius: 0.3em;
    border: 1px solid rgba(255, 255, 255, 0.25);
    font-family: inherit;
  }
`;

const Caption = styled(motion.div)`
  position: relative;
  overflow: hidden;
  margin-bottom: 0.9rem;
  padding: 0.8rem 0.9rem 1rem;
  border-radius: 0.9rem;
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.45);

  small {
    display: block;
    color: ${colors.accent};
    font-family: 'RedHatDisplay-Bold';
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-size: 0.7rem;
  }

  strong {
    display: block;
    font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
    font-size: 1.15rem;
    margin: 0.15rem 0 0.3rem;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.86);
    font-size: 0.85rem;
    line-height: 1.45;
  }
`;

const CaptionIdle = styled.div`
  margin-bottom: 0.9rem;
  padding: 0.8rem 0.9rem;
  border-radius: 0.9rem;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  color: ${colors.muted};
  font-size: 0.82rem;
  line-height: 1.45;
`;

const DockToggle = styled(Button)`
  ${glass}
  position: fixed;
  z-index: 60;
  left: 0;
  top: 50%;
  translate: 0 -50%;
  border-radius: 0 999px 999px 0;
  border-left: 0;
`;

const PHONE_W = 390;
const PHONE_H = 844;
const PHONE_SCALE = 0.56;

const PhoneColumn = styled(motion.aside)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  min-width: 0;
  overflow: hidden;
  color: ${colors.muted};
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-left: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 900px) {
    display: none;
  }
`;

const Phone = styled.div`
  flex-shrink: 0;
  width: ${PHONE_W * PHONE_SCALE + 20}px;
  height: ${PHONE_H * PHONE_SCALE + 20}px;
  padding: 10px;
  border-radius: 34px;
  background: #0b0b0f;
  box-shadow:
    0 2rem 5rem rgba(0, 0, 0, 0.6),
    inset 0 0 0 2px #2a2a33;

  iframe {
    width: ${PHONE_W}px;
    height: ${PHONE_H}px;
    border: 0;
    border-radius: ${24 / PHONE_SCALE}px;
    transform: scale(${PHONE_SCALE});
    transform-origin: top left;
    background: ${colors.background};
  }
`;

const isViewer = () =>
  new URLSearchParams(window.location.search).has('viewer');

/**
 * Demo do telão (`/telao/demo`): o telão real alimentado por um torneio
 * simulado, com tour automático e um botão para cada momento animado. A
 * moldura de celular (`?viewer=1`) espelha a mesma demo via BroadcastChannel.
 */
const TournamentBoardDemo: React.FC = () => {
  const [viewer] = useState(isViewer);
  const [run, setRun] = useState(0);
  const [stepIndex, setStepIndex] = useState<number | null>(null);
  const [stepRun, setStepRun] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [dockOpen, setDockOpen] = useState(() => window.innerWidth > 900);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [chromeHidden, setChromeHidden] = useState(false);
  const [captionOn, setCaptionOn] = useState(false);

  const model = useRef(new DemoTournament());
  const last = useRef<IBoardSnapshot | null>(
    viewer ? null : model.current.snapshot(),
  );
  const status = useRef<TConnectionStatus>('live');
  const handlers = useRef<IBoardSourceHandlers | null>(null);
  const timers = useRef<number[]>([]);
  const channel = useRef<BroadcastChannel | null>(null);

  const post = (message: TMessage) => channel.current?.postMessage(message);

  const clearTimers = () => {
    timers.current.forEach(id => window.clearTimeout(id));
    timers.current = [];
  };

  const controls = useRef<IDemoControls>({
    model: () => model.current,
    emit: snapshot => {
      last.current = snapshot;
      handlers.current?.onSnapshot(snapshot);
      post({ type: 'snapshot', snapshot });
    },
    setStatus: next => {
      status.current = next;
      handlers.current?.onStatus(next);
      post({ type: 'status', status: next });
    },
    reset: () => {
      clearTimers();
      model.current = new DemoTournament();
      last.current = model.current.snapshot();
      status.current = 'live';
      setRun(r => r + 1);
      post({ type: 'reset', snapshot: last.current });
    },
    later: (fn, ms) => {
      timers.current.push(window.setTimeout(fn, ms));
    },
  });

  // O telão remonta a cada `run` (key), e cada montagem pede a fonte de novo:
  // recebe o status e a última versão emitida.
  const source = useCallback<TBoardSource>(h => {
    handlers.current = h;
    h.onStatus(status.current);
    if (last.current) h.onSnapshot(last.current);
    return () => {
      if (handlers.current === h) handlers.current = null;
    };
  }, []);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const bc = new BroadcastChannel(CHANNEL);
    channel.current = bc;

    bc.onmessage = ({ data }: MessageEvent<TMessage>) => {
      if (!viewer) {
        if (data.type === 'hello' && last.current) {
          bc.postMessage({ type: 'reset', snapshot: last.current });
        }
        return;
      }
      if (data.type === 'reset') {
        last.current = data.snapshot;
        status.current = 'live';
        setRun(r => r + 1);
      } else if (data.type === 'snapshot') {
        last.current = data.snapshot;
        handlers.current?.onSnapshot(data.snapshot);
      } else if (data.type === 'status') {
        status.current = data.status;
        handlers.current?.onStatus(data.status);
      }
    };

    if (viewer) bc.postMessage({ type: 'hello' });
    return () => {
      bc.close();
      channel.current = null;
    };
  }, [viewer]);

  useEffect(() => clearTimers, []);

  const runStep = useCallback((index: number) => {
    DEMO_STEPS[index].run(controls.current);
    setStepIndex(index);
    setStepRun(n => n + 1);
    setCaptionOn(true);
  }, []);

  // Tour: executa o passo e agenda o próximo; a legenda some no fim do passo.
  useEffect(() => {
    if (stepIndex === null) return undefined;
    const timer = window.setTimeout(() => {
      if (playing && stepIndex < DEMO_STEPS.length - 1) {
        runStep(stepIndex + 1);
      } else {
        setPlaying(false);
        setCaptionOn(false);
      }
    }, DEMO_STEPS[stepIndex].holdMs);
    return () => window.clearTimeout(timer);
  }, [stepRun, stepIndex, playing, runStep]);

  const togglePlay = useCallback(() => {
    if (playing) {
      setPlaying(false);
      return;
    }
    setPlaying(true);
    const resume =
      stepIndex === null || stepIndex >= DEMO_STEPS.length - 1
        ? 0
        : stepIndex + 1;
    runStep(resume);
  }, [playing, stepIndex, runStep]);

  const restart = useCallback(() => {
    setPlaying(false);
    setCaptionOn(false);
    setStepIndex(null);
    controls.current.reset();
  }, []);

  useEffect(() => {
    if (viewer) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLButtonElement && event.key === ' ') {
        event.preventDefault();
      }
      const key = event.key.toLowerCase();
      if (key === ' ') togglePlay();
      else if (key === 'arrowright')
        runStep(Math.min(DEMO_STEPS.length - 1, (stepIndex ?? -1) + 1));
      else if (key === 'arrowleft') runStep(Math.max(0, (stepIndex ?? 1) - 1));
      else if (key === 'r') restart();
      else if (key === 'd') setDockOpen(open => !open);
      else if (key === 'c') setPhoneOpen(open => !open);
      else if (key === 'h') setChromeHidden(hidden => !hidden);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewer, togglePlay, runStep, restart, stepIndex]);

  const board = <TournamentBoard key={run} source={source} />;
  if (viewer) return board;

  const step = stepIndex === null ? null : DEMO_STEPS[stepIndex];
  const showChrome = !chromeHidden;

  const dockShown = showChrome && dockOpen;
  const phoneShown = showChrome && phoneOpen;

  return (
    <Stage $dock={dockShown} $phone={phoneShown}>
      <AnimatePresence initial={false}>
        {dockShown ? (
          <Dock
            key="dock"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
          >
            <h3>Demo do telão</h3>
            <p>
              O telão real com um torneio simulado. Rode o tour ou clique num
              momento para ver a animação.
            </p>
            <Controls>
              <Button type="button" $primary onClick={togglePlay}>
                {playing ? <PauseIcon aria-hidden /> : <PlayIcon aria-hidden />}
                {playing ? 'Pausar tour' : 'Tour automático'}
              </Button>
              <Button type="button" onClick={restart}>
                <UndoIcon aria-hidden />
                Reiniciar
              </Button>
              <Button
                type="button"
                $on={phoneOpen}
                aria-pressed={phoneOpen}
                onClick={() => setPhoneOpen(open => !open)}
              >
                Celular
              </Button>
              <Button type="button" onClick={() => setDockOpen(false)}>
                Ocultar
              </Button>
            </Controls>

            <AnimatePresence mode="wait" initial={false}>
              {captionOn && step ? (
                <Caption
                  key={stepRun}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
                  aria-live="polite"
                >
                  <small>
                    Momento {(stepIndex ?? 0) + 1} de {DEMO_STEPS.length}
                  </small>
                  <strong>{step.title}</strong>
                  <p>{step.detail}</p>
                  <StepProgress
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{
                      duration: step.holdMs / 1000,
                      ease: 'linear',
                    }}
                  />
                </Caption>
              ) : (
                <CaptionIdle key="idle">
                  Aqui aparece o que observar em cada momento enquanto ele
                  acontece no telão.
                </CaptionIdle>
              )}
            </AnimatePresence>

            <StepList>
              {DEMO_STEPS.map((item, index) => (
                <li key={item.id}>
                  <StepButton
                    type="button"
                    $active={index === stepIndex && captionOn}
                    onClick={() => runStep(index)}
                  >
                    <b>{index + 1}</b>
                    {item.title}
                  </StepButton>
                </li>
              ))}
            </StepList>
            <Keys>
              <kbd>espaço</kbd> tour · <kbd>←</kbd>
              <kbd>→</kbd> passos · <kbd>R</kbd> reiniciar · <kbd>C</kbd>{' '}
              celular · <kbd>D</kbd> painel · <kbd>H</kbd> esconder tudo
            </Keys>
          </Dock>
        ) : (
          <div key="no-dock" />
        )}
      </AnimatePresence>

      <BoardCell>{board}</BoardCell>

      {phoneShown ? (
        <PhoneColumn
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
        >
          <Phone>
            <iframe
              title="Telão no celular"
              src={`${window.location.pathname}?viewer=1`}
            />
          </Phone>
          Celular · mesma demo
        </PhoneColumn>
      ) : (
        <div />
      )}

      {showChrome && !dockOpen && (
        <DockToggle type="button" onClick={() => setDockOpen(true)}>
          Demo
          <CaretRightIcon aria-hidden />
        </DockToggle>
      )}
    </Stage>
  );
};

export default TournamentBoardDemo;
